<template>
  <div v-if="hasAccess" class="space-y-6 pb-10">
    <page-content>
      <section class="space-y-6">
        <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 class="text-xl font-semibold text-blue-600">Domaines sans connexion</h1>
            <p class="mt-1 text-sm text-gray-600">
              Aucun login administrateur/observateur détecté sur la période sélectionnée.
            </p>
            <p class="mt-1 text-sm text-gray-600">{{ periodLabel }}</p>
          </div>
          <button
            type="button"
            class="inline-flex items-center rounded-lg border border-blue-200 bg-white px-4 py-2 text-sm font-semibold text-blue-600 transition hover:bg-blue-50"
            @click="goBack"
          >
            Retour aux connexions
          </button>
        </div>

        <div class="grid gap-4 md:grid-cols-3">
          <article class="rounded-2xl border border-blue-200 bg-white px-5 py-4 shadow-sm">
            <p class="text-sm font-medium text-gray-600">Sans connexion</p>
            <p class="mt-3 text-3xl font-semibold text-gray-900">{{ formatNumber(domainsWithoutConnections.length) }}</p>
          </article>
          <article class="rounded-2xl border border-blue-200 bg-white px-5 py-4 shadow-sm">
            <p class="text-sm font-medium text-gray-600">Domaines actifs</p>
            <p class="mt-3 text-3xl font-semibold text-gray-900">{{ formatNumber(totals.openDomains) }}</p>
          </article>
          <article class="rounded-2xl border border-blue-200 bg-white px-5 py-4 shadow-sm">
            <p class="text-sm font-medium text-gray-600">Domaines avec connexion</p>
            <p class="mt-3 text-3xl font-semibold text-gray-900">{{ formatNumber(totals.domainsWithConnections) }}</p>
          </article>
        </div>

        <section class="rounded-2xl border border-blue-200 bg-white shadow-sm">
          <header class="border-b border-gray-200 px-5 py-4">
            <h2 class="text-lg font-semibold text-gray-900">Liste des domaines</h2>
          </header>
          <div class="overflow-x-auto px-2 py-3">
            <table class="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th class="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Domaine</th>
                  <th class="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-600">Admins</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-if="isLoading">
                  <td colspan="2" class="px-3 py-6 text-sm text-gray-600">Chargement…</td>
                </tr>
                <tr v-else-if="domainsWithoutConnections.length === 0">
                  <td colspan="2" class="px-3 py-6 text-sm text-gray-600">
                    Tous les domaines filtrés ont eu au moins une connexion.
                  </td>
                </tr>
                <tr v-for="domain in domainsWithoutConnections" :key="domain.domain_uuid">
                  <td class="px-3 py-3 text-sm text-gray-900">{{ domain.domain_label }}</td>
                  <td class="px-3 py-3 text-right text-sm font-medium text-gray-900">{{ formatNumber(domain.admins) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </section>
    </page-content>
  </div>
  <div v-else class="px-6 py-10">
    <p class="text-sm text-gray-600">
      Vous n'avez pas accès à cette section.
    </p>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { format, parseISO, subDays } from 'date-fns'
import fr from 'date-fns/locale/fr'
import PageContent from '@/components/PageContent.vue'
import { http } from '@/services/api.service'

const DATE_FORMAT = 'yyyy-MM-dd'

function toIsoDate (date) {
  return format(date, DATE_FORMAT)
}

function isValidIsoDate (value) {
  if (!value || typeof value !== 'string') {
    return false
  }

  try {
    const parsed = parseISO(value)
    return parsed instanceof Date && !Number.isNaN(parsed.getTime())
  } catch (error) {
    return false
  }
}

export default {
  name: 'app-dashboard-connection-inactive-domains',
  components: {
    PageContent
  },
  data () {
    const end = new Date()
    const start = subDays(end, 29)

    return {
      isLoading: false,
      dateStart: toIsoDate(start),
      dateEnd: toIsoDate(end),
      selectedDomainUuids: [],
      domainsWithoutConnections: [],
      totals: {
        openDomains: 0,
        domainsWithConnections: 0
      }
    }
  },
  computed: {
    ...mapGetters(['roles']),
    hasAccess () {
      return this.roles.includes('admin') || this.roles.includes('community-manager')
    },
    periodLabel () {
      if (!isValidIsoDate(this.dateStart) || !isValidIsoDate(this.dateEnd)) {
        return ''
      }

      return `Période du ${format(parseISO(this.dateStart), 'dd/MM/yyyy', { locale: fr })} au ${format(parseISO(this.dateEnd), 'dd/MM/yyyy', { locale: fr })}`
    }
  },
  async created () {
    const hasAccess = await this.ensureAccess()
    if (!hasAccess) {
      this.$router.replace('/app/dashboard')
      return
    }

    this.applyRouteFilters()
    this.loadSummary()
  },
  methods: {
    async ensureAccess () {
      if (!Array.isArray(this.roles) || this.roles.length === 0) {
        try {
          await this.$store.dispatch('FETCH_ROLES')
        } catch (error) {
          return false
        }
      }

      return this.hasAccess
    },
    applyRouteFilters () {
      const today = new Date()
      const fallbackStart = toIsoDate(subDays(today, 29))
      const fallbackEnd = toIsoDate(today)

      const queryDateStart = this.$route?.query?.dateStart
      const queryDateEnd = this.$route?.query?.dateEnd
      const queryDomains = this.$route?.query?.domains

      this.dateStart = isValidIsoDate(queryDateStart) ? queryDateStart : fallbackStart
      this.dateEnd = isValidIsoDate(queryDateEnd) ? queryDateEnd : fallbackEnd

      if (Array.isArray(queryDomains)) {
        this.selectedDomainUuids = queryDomains.filter(Boolean)
      } else if (typeof queryDomains === 'string' && queryDomains) {
        this.selectedDomainUuids = [queryDomains]
      } else {
        this.selectedDomainUuids = []
      }
    },
    loadSummary () {
      this.isLoading = true

      const params = {
        summaryOnly: 1,
        dateStart: this.dateStart,
        dateEnd: this.dateEnd
      }

      if (Array.isArray(this.selectedDomainUuids) && this.selectedDomainUuids.length) {
        params.domains = [...this.selectedDomainUuids]
      }

      http
        .get('/dashboard/login-stats', { params })
        .then(({ data }) => {
          const summary = data?.summary || {}
          const totals = summary?.totals || {}

          this.totals = {
            openDomains: Number(totals?.openDomains || 0),
            domainsWithConnections: Number(totals?.domainsWithConnections || 0)
          }

          this.domainsWithoutConnections = Array.isArray(summary?.domainsWithoutConnections)
            ? summary.domainsWithoutConnections
            : []
        })
        .catch(() => {
          this.totals = {
            openDomains: 0,
            domainsWithConnections: 0
          }
          this.domainsWithoutConnections = []
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    goBack () {
      const query = {
        dateStart: this.dateStart,
        dateEnd: this.dateEnd
      }

      if (Array.isArray(this.selectedDomainUuids) && this.selectedDomainUuids.length) {
        query.domains = [...this.selectedDomainUuids]
      }

      this.$router.push({
        name: 'app-dashboard-tracking-elected-connections',
        query
      })
    },
    formatNumber (value) {
      return new Intl.NumberFormat('fr-FR').format(Number(value || 0))
    }
  }
}
</script>
